import dayjs, { type ManipulateType } from 'dayjs';

const DURATION_UNIT_TO_DAYJS: Record<string, ManipulateType> = {
  YEAR: 'year',
  MONTH: 'month',
  WEEK: 'week',
  DAY: 'day',
  HOUR: 'hour',
  MINUTE: 'minute',
  SECOND: 'second',
};

type Duration = { amount: number; unit: string };

type NoticePeriodBound = { referenceDate?: string | null; duration: Duration };

export type StopReasonValidity = {
  appliedAtDate?: string | null;
  appliedInFutureAllowed?: boolean | null;
  validForDuration?: Duration | null;
  requestNoticePeriod?: { duration: Duration } | null;
  stopDateNoticePeriod?: {
    min?: NoticePeriodBound | null;
    max?: NoticePeriodBound | null;
  } | null;
};

/** The policy dates a stop reason's notice periods can be anchored to. */
export type StopReasonPolicyDates = {
  startedAt?: string;
  nextRenewalAt?: string;
  suspendedAt?: string;
};

/**
 * Resolves a stop-date bound to its anchor plus duration. An anchor the policy cannot supply
 * (SUSPENSION_STARTED while not suspended) resolves to undefined, closing the reason.
 */
const resolveBoundDate = (
  bound: NoticePeriodBound | null | undefined,
  policy: StopReasonPolicyDates,
): Date | undefined => {
  const unit = bound && DURATION_UNIT_TO_DAYJS[bound.duration.unit];
  if (!bound || !unit) {
    return undefined;
  }
  if (bound.referenceDate === 'SUSPENSION_STARTED') {
    // day-start matches the backend anchor, but in the browser zone: policy.timeZone is not plumbed
    return policy.suspendedAt
      ? dayjs(policy.suspendedAt).startOf('day').add(bound.duration.amount, unit).toDate()
      : undefined;
  }
  // An anchor this build does not know closes the reason rather than silently becoming "today".
  if (bound.referenceDate && bound.referenceDate !== 'NOW') {
    return undefined;
  }
  return dayjs().startOf('day').add(bound.duration.amount, unit).toDate();
};

export type StopReasonUnavailability =
  | { cause: 'WINDOW_ELAPSED'; since: Date }
  | { cause: 'NOTICE_DEADLINE_PASSED'; since: Date }
  | { cause: 'ANCHOR_UNAVAILABLE' };

/**
 * Why a stop reason cannot be used, or undefined when it can. The funnel shows the cause on the
 * disabled card; the backend rejects the same cases with INVALID_STOP_REASON_DETAILS.
 */
export const getStopReasonUnavailability = ({
  reason,
  policy,
}: {
  reason: StopReasonValidity;
  policy: StopReasonPolicyDates;
}): StopReasonUnavailability | undefined => {
  const window = reason.validForDuration;
  if (window && policy.startedAt) {
    const unit = DURATION_UNIT_TO_DAYJS[window.unit];
    const expiresAt = unit ? dayjs(policy.startedAt).add(window.amount, unit) : undefined;
    if (expiresAt && dayjs().isAfter(expiresAt)) {
      return { cause: 'WINDOW_ELAPSED', since: expiresAt.toDate() };
    }
  }

  const requestNoticePeriod = reason.requestNoticePeriod;
  if (requestNoticePeriod && policy.nextRenewalAt) {
    const unit = DURATION_UNIT_TO_DAYJS[requestNoticePeriod.duration.unit];
    const deadline = unit
      ? dayjs(policy.nextRenewalAt).add(requestNoticePeriod.duration.amount, unit)
      : undefined;
    if (deadline && !dayjs().isBefore(deadline)) {
      return { cause: 'NOTICE_DEADLINE_PASSED', since: deadline.toDate() };
    }
  }

  const { min, max } = reason.stopDateNoticePeriod ?? {};
  if ((min && !resolveBoundDate(min, policy)) || (max && !resolveBoundDate(max, policy))) {
    return { cause: 'ANCHOR_UNAVAILABLE' };
  }

  return undefined;
};

/** The at-me fields that decide which stop reasons an actor may use. */
export type StopReasonActor = {
  brokerRole?: string;
  brokerageFirmId?: string;
  relations?: { resourceId: string; relationType: string }[];
};

/**
 * Who a stop's cancellation is attributed to. The backend stores this verbatim as the branch's
 * closeReason and never normalises it, so a superuser — who has no brokerageFirmId yet is
 * authorised to invalidate a stop branch — must not be filed as the customer.
 */
export const getBranchCloseReason = (user: {
  brokerageFirmId?: string;
  isSuperUser?: boolean;
}): 'BROKER_REQUEST' | 'CUSTOMER_REQUEST' =>
  user.brokerageFirmId || user.isSuperUser ? 'BROKER_REQUEST' : 'CUSTOMER_REQUEST';

/**
 * The role a stop reason's `allowedTo` is matched against: the broker role for a broker of the
 * policy's own firm, as the backend reads it, otherwise the actor's relation to one of the
 * policy's customers. Undefined for someone who is neither: only unrestricted reasons remain.
 */
export const getBusinessRoleId = ({
  user,
  policy,
}: {
  user: StopReasonActor;
  policy: { customers?: string[]; brokerageFirm?: string };
}): string | undefined =>
  (user.brokerageFirmId && user.brokerageFirmId === policy.brokerageFirm
    ? user.brokerRole
    : undefined) ??
  user.relations?.find(relation => policy.customers?.includes(relation.resourceId))?.relationType;

/**
 * A reason without `allowedTo` is open to everyone; one with it is reserved to the listed roles.
 * The backend enforces the same rule with INVALID_STOP_REASON, so an unfiltered list would only
 * offer options that always fail.
 */
export const isStopReasonAllowedTo = ({
  reason,
  businessRoleId,
}: {
  reason: { allowedTo?: string[] };
  businessRoleId?: string;
}): boolean =>
  !reason.allowedTo?.length || (!!businessRoleId && reason.allowedTo.includes(businessRoleId));

export const isStopReasonAvailable = ({
  reason,
  policy,
}: {
  reason: StopReasonValidity;
  policy: StopReasonPolicyDates;
}): boolean => !getStopReasonUnavailability({ reason, policy });

/**
 * A reason with a requestNoticePeriod but no appliedAtDate takes no stopDate input at all — the
 * backend computes the effective date itself (the policy's next anniversary). Hide the date picker
 * for such reasons.
 */
export const hasNoStopDateInput = (reason: StopReasonValidity): boolean =>
  !!reason.requestNoticePeriod && !reason.appliedAtDate;

/**
 * For a reason with a stopDateNoticePeriod.min, the date picker's earliest selectable date is its
 * anchor + duration. Reasons without one keep their existing floor.
 */
export const getStopReasonMinDate = ({
  reason,
  policy,
}: {
  reason: StopReasonValidity;
  policy: StopReasonPolicyDates;
}): Date | undefined => resolveBoundDate(reason.stopDateNoticePeriod?.min, policy);

/**
 * The date picker's latest selectable date: the stopDateNoticePeriod.max anchor + duration, and —
 * unless the reason allows future dates — today, as the backend rejects any later instant.
 */
export const getStopReasonMaxDate = ({
  reason,
  policy,
}: {
  reason: StopReasonValidity;
  policy: StopReasonPolicyDates;
}): Date | undefined => {
  const noticePeriodMax = resolveBoundDate(reason.stopDateNoticePeriod?.max, policy);
  if (reason.appliedInFutureAllowed) {
    return noticePeriodMax;
  }
  const now = dayjs();
  return noticePeriodMax && now.isAfter(noticePeriodMax) ? noticePeriodMax : now.toDate();
};

/** Everything a stop-date picker needs from a reason, before or after one is picked. */
export const getStopDatePickerProps = ({
  reason,
  policy,
}: {
  reason?: StopReasonValidity;
  policy: StopReasonPolicyDates;
}) => ({
  fromDate:
    (reason && getStopReasonMinDate({ reason, policy })) ??
    (policy.startedAt ? new Date(policy.startedAt) : undefined),
  toDate: reason ? getStopReasonMaxDate({ reason, policy }) : undefined,
});

export type StopDateViolation = {
  cause: 'NOT_AFTER_POLICY_START' | 'IN_FUTURE' | 'BEFORE_MIN' | 'AFTER_MAX';
  limit: Date;
};

/** The backend's stop-date checks (validateStopDateInputs), in its order; undefined when valid. */
export const getStopDateViolation = ({
  reason,
  policy,
  stopDate,
  now = new Date(),
}: {
  reason: StopReasonValidity;
  policy: StopReasonPolicyDates;
  stopDate: string;
  now?: Date;
}): StopDateViolation | undefined => {
  const date = dayjs(stopDate);
  if (policy.startedAt && !date.isAfter(policy.startedAt)) {
    return { cause: 'NOT_AFTER_POLICY_START', limit: new Date(policy.startedAt) };
  }
  if (!reason.appliedInFutureAllowed && date.isAfter(now)) {
    return { cause: 'IN_FUTURE', limit: now };
  }
  const min = resolveBoundDate(reason.stopDateNoticePeriod?.min, policy);
  if (min && date.isBefore(min)) {
    return { cause: 'BEFORE_MIN', limit: min };
  }
  const max = resolveBoundDate(reason.stopDateNoticePeriod?.max, policy);
  if (max && date.isAfter(max)) {
    return { cause: 'AFTER_MAX', limit: max };
  }
  return undefined;
};
